import { datadogRum } from '@datadog/browser-rum';
import { Bolt, ListAlt } from '@mui/icons-material';
import Typography from '@mui/material/Typography';
import { useEffect } from 'react';
import { navigateTo } from '@/utils/navigation';
import { NeedsAttentionCard } from '@/views/Home/components/NeedsAttention/components/NeedsAttentionCard';
import {
  TrackHomePageEvent,
  useTrackHomePageAnalytics,
} from '$analytics/useTrackAnalytics/domains/homePage';
import type { PaymentRequest } from '$api-clients/hooks';

function InstantPayEligibleIndicator() {
  return (
    <>
      <Bolt color="warning" />

      <Typography
        sx={{
          fontSize: '10px',
          letterSpacing: '0.24px',
        }}
      >
        Instant Pay Eligible
      </Typography>
    </>
  );
}

type CommissionFormsAwaitingActionProps = {
  interactionCount: number;
  commissions: PaymentRequest<'/commissions/'>['Data']['results'];
  singleCommissionFormUrl?: string;
};

export function CommissionFormsAwaitingAction({
  interactionCount = 0,
  commissions,
  singleCommissionFormUrl,
}: CommissionFormsAwaitingActionProps) {
  const trackEvent = useTrackHomePageAnalytics();

  const notSubmittedCount = commissions.filter(
    (c) => !c.hasBeenSubmitted,
  ).length;

  const readyToCertifyCount = commissions.filter(
    ({ paymentStatus }) => paymentStatus === 'AWAITING_CERTIFICATION',
  ).length;

  const instantPayEligible = commissions.some(({ me, paymentStatus }) => {
    const isAwaitingCertification = paymentStatus === 'AWAITING_CERTIFICATION';
    const canEnableInstantPay = me.additionalPermissions.includes(
      'CAN_EDIT_INSTANT_PAY_CERTIFICATION',
    );

    return isAwaitingCertification && canEnableInstantPay;
  });

  const supplementaryDetails = [];

  if (interactionCount > 10) {
    supplementaryDetails.push(
      'Forms need submission or are ready to certify for payment',
    );
  } else {
    if (notSubmittedCount) {
      supplementaryDetails.push(`${notSubmittedCount} Not Submitted`);
    }

    if (readyToCertifyCount) {
      supplementaryDetails.push(`${readyToCertifyCount} Ready to Certify`);
    }
  }

  const routeToPayments = () => {
    trackEvent(TrackHomePageEvent.NeedsAttentionCardClick, {
      numItem: interactionCount,
      eventTarget: 'commission',
      isInstantPay: instantPayEligible,
    });

    if (interactionCount > 1) {
      navigateTo('/payment?tab=NEEDS_ATTENTION&utm_source=hp&utm_medium=nac');
    } else if (singleCommissionFormUrl) {
      navigateTo(singleCommissionFormUrl);
    } else {
      datadogRum.addError(
        new Error(
          'There was a problem routing to the payments page from the needs attention card',
        ),
      );
    }
  };

  useEffect(() => {
    if (!interactionCount) return;

    trackEvent(TrackHomePageEvent.NeedsAttentionCardMount, {
      numItem: interactionCount,
      eventTarget: 'commission',
      isInstantPay: instantPayEligible,
    });
  });

  return (
    <NeedsAttentionCard
      itemCount={interactionCount}
      color="error"
      headerEndAdornment={instantPayEligible && <InstantPayEligibleIndicator />}
      headerStartAdornment={<ListAlt />}
      onClick={routeToPayments}
      supplementaryDetails={supplementaryDetails}
      title="Commission Forms Awaiting Action"
    />
  );
}

CommissionFormsAwaitingAction.displayName = 'CommissionFormsAwaitingAction';
